import { useEffect, useState } from "react";
import { Languages } from "lucide-react";
import { api, toastErr } from "@/lib/api";

let LANGS = null;
function useLangs() {
  const [l, setL] = useState(LANGS || []);
  useEffect(() => { if (!LANGS) api.get("/translate/languages").then((r) => { LANGS = r.data; setL(r.data); }).catch(() => {}); }, []);
  return l;
}
// Translation only when a user chose the client's language for this chat; never automatic. "IT" = Nessuna traduzione.
export const langOf = (tr) => tr?.lang || "IT";

export function TranslationBar({ conv, onChange }) {
  const langs = useLangs();
  const lang = langOf(conv.tr);
  const on = lang !== "IT";
  const name = (langs.find((l) => l.code === lang)?.name || lang).toLowerCase();
  const set = async (code) => {
    const prev = conv.tr;
    onChange({ ...conv, tr: code ? { lang: code } : {} }); // header + composer switch AT ONCE, the server confirms
    try { const { data } = await api.patch(`/conversations/${conv.id}/translation`, { lang: code }); onChange({ ...conv, tr: data.tr }); } catch (e) { onChange({ ...conv, tr: prev }); toastErr(e); }
  };
  // ONE compact menu in the chat header: "文A Traduzione: nessuna ▾"
  return (
    <label className={`relative shrink-0 flex items-center gap-1 rounded-md border px-1.5 h-7 text-[11px] cursor-pointer transition-colors ${on ? "border-sky-500/40 text-sky-300" : "border-[#232738] text-slate-400 hover:text-white"}`} title="Lingua del cliente" data-testid="translation-bar">
      <Languages className={`h-3.5 w-3.5 shrink-0 ${on ? "text-sky-400" : "text-slate-500"}`} />
      <span className="hidden xl:inline whitespace-nowrap" data-testid="translation-status">Traduzione: {on ? name : "nessuna"}</span>
      <select value={on ? lang : ""} onChange={(e) => set(e.target.value)} aria-label="Lingua del cliente" className="absolute inset-0 opacity-0 cursor-pointer" data-testid="translation-lang-select">
        <option value="">Nessuna traduzione</option>
        {langs.map((l) => <option key={l.code} value={l.code}>{l.name.charAt(0).toUpperCase() + l.name.slice(1)}</option>)}
      </select>
      <span className="text-[9px]">▾</span>
    </label>
  );
}

// "Il cliente riceverà: …" computed while typing (300 ms after the last key): the send uses it as is, no DeepL wait at Invio.
export function useOutPreview(convId, text, on) {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!on || !text.trim()) { setP(null); return undefined; }
    const t = setTimeout(() => api.post(`/conversations/${convId}/translate-preview`, { text }).then((r) => setP(r.data)).catch(() => setP(null)), 300);
    return () => clearTimeout(t);
  }, [convId, text, on]);
  return p;
}

export function DeeplUsage() {
  const [u, setU] = useState(null);
  useEffect(() => { api.get("/translate/usage").then((r) => setU(r.data)).catch(() => {}); }, []);
  if (!u) return null;
  const warn = u.ok && u.pct >= 80;
  return (
    <div className="mt-6 rounded-xl border border-[#232738] bg-[#0D0E14] p-4 text-sm" data-testid="deepl-usage">
      <div className="flex items-center gap-2 font-semibold"><Languages className="h-4 w-4 text-sky-400" />Traduzione DeepL</div>
      {u.ok ? <>
        <div className={`mt-1 num ${warn ? "text-amber-300" : "text-slate-300"}`} data-testid="deepl-usage-value">{u.used.toLocaleString("it-IT")} / {u.limit.toLocaleString("it-IT")} caratteri usati nel mese ({u.pct ?? 0}%) · rimasti {u.left.toLocaleString("it-IT")}</div>
        {u.resets_at && <div className="text-xs text-slate-500">Si azzerano il {new Date(u.resets_at).toLocaleDateString("it-IT")}</div>}
        {u.used >= u.limit && <div className="mt-1 font-bold text-red-300" data-testid="deepl-finished">Crediti DeepL finiti</div>}
      </> : <div className="mt-1 font-bold text-red-300" data-testid="deepl-usage-error">{u.error}</div>}
      <div className="mt-1 text-xs text-slate-500" data-testid="deepl-today">Oggi: {u.ok_today || 0} traduzioni riuscite, {u.failed_today || 0} non riuscite</div>
    </div>
  );
}
